import React from 'react';
import axios from 'axios';
import { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { GQLResponseError, SDError } from '../../model/Errors';
import { responseHasErrors } from '../../utils/ApiHelper';
import history from '../../history';
import TimeoutError from '../../model/TimeoutError';
import { AppLocaleProvider } from 'sd-ui-components';
import IdleTimer from 'react-idle-timer';
import { useOidc } from '@axa-fr/react-oidc';

const { injectIntl } = AppLocaleProvider;
const timeoutErrorCode = 'ECONNABORTED';

/**
 * This component sets up app level configurations.
 */
@inject('AuthStore')
@inject('AuthZStore')
@inject('ConfigStore')
@inject('NotificationsStore')
@observer
class AppConfig extends Component {
  state = {
    ready: false,
    isTimedOut: false
  };

  idleTimer = React.createRef();

  componentDidMount() {
    window.addEventListener('storage', this.checkSession);
    this.setupWatchers();
    this.setState({ ready: true });
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.checkSession);
  }

  componentDidCatch(error, info) {
    console.error(error);
    this.goToErrorPage();
  }

  setupWatchers() {
    this.createInterceptors();
  }

  goToErrorPage(error) {
    console.error(error);
    history.push('/error');
  }

  checkSession = () => {
    const accessTokenObj = JSON.parse(localStorage.getItem('oidc.default'));
    const gp2token =
      accessTokenObj && accessTokenObj.tokens !== null
        ? accessTokenObj.tokens.accessToken
        : null;
    if (!gp2token) {
      this.logoutUser();
    }
  };

  handleOnActive = e => {
    this.setState({ isTimedOut: false });
  };

  handleOnAction = e => {
    this.setState({ isTimedOut: false });
  };

  handleOnIdle = e => {
    const { isTimedOut } = this.state;
    if (isTimedOut) {
      this.logoutUser();
    } else {
      this.idleTimer.current.reset();
      this.setState({ isTimedOut: true });
    }
  };

  logoutUser = () => {
    const { isAuthenticated, logout } = useOidc();
    if (isAuthenticated && !this.props.AuthStore.loginInfo['appLauncher']) {
      logout('/');
      this.props.AuthStore.removeStorageForLogout();
    }
  };

  /**
   * Takes a GQLResponseError, which contains an array of GQL errors, and mutates them to be handled by
   * handleAjaxError to be processed individually.
   * @param {GQLResponseError} gqlErrorObject
   */
  handleGQLError(gqlErrorObject) {
    gqlErrorObject.errors
      .map(error => {
        if (error.data || error.extensions) {
          const status = error.data
            ? error.data.httpStatusCode
            : error.extensions.code;
          return {
            response: {
              status
            }
          };
        } else {
          return new SDError('Unknown GQL error');
        }
      })
      .forEach(error => this.handleAjaxError(error));
  }

  handleAjaxError(error) {
    const { isAuthenticated, logout } = useOidc();
    if (error.response) {
      switch (error.response.status) {
        case 403:
          if (isAuthenticated) {
            logout('/unauthorized');
            this.removeStorageForLogout();
            return;
          } else {
            this.props.AuthZStore.handleUnauthorized();
          }
          break;
        case 401:
          if (isAuthenticated) {
            logout('/unauthorized');
            this.removeStorageForLogout();
            return;
          } else {
            this.props.AuthStore.logout();
          }
          break;
        case 504:
          this.handleTimeoutError();
          break;
        default:
          this.goToErrorPage(error);
      }
    } else {
      this.goToErrorPage(error);
    }
  }

  /**
   * Timeouts will add a notification instead of redirecting.
   */
  handleTimeoutError() {
    this.props.NotificationsStore.pushNotification(
      new TimeoutError(
        this.props.intl.formatMessage({
          id: 'App.Error.Timeout',
          defaultMessage:
            'The server encountered an error while trying to load this page. Please try again.'
        })
      )
    );
  }

  createInterceptors() {
    /**
     * Request interceptor for adding bearer token, timeout, and id token.
     */
    axios.interceptors.request.use(config => {
      config.headers['Authorization'] = `Bearer ${this.props.AuthStore
        .accessToken || undefined}`;

      config.headers[
        this.props.ConfigStore.config[
          this.props.ConfigStore.configurationKeys.idTokenCustomHeader
        ]
      ] = this.props.AuthStore.idToken || undefined;

      config.timeout =
        this.props.ConfigStore.config[
          this.props.ConfigStore.configurationKeys
            .defaultRequestTimeoutInMilliseconds
        ] || 0;

      return config;
    });

    /**
     * Response interceptor for handling errors.
     */
    axios.interceptors.response.use(
      response => {
        if (responseHasErrors(response)) {
          const error = new GQLResponseError('GQL Error', response.data.errors);
          this.handleGQLError(error);
          throw error;
        }

        return response;
      },
      error => {
        if (axios.isCancel(error)) {
          // Do nothing.
          console.info('Request cancelled', error);
        } else if (error instanceof GQLResponseError) {
          this.handleGQLError(error);
        } else if (error.response && responseHasErrors(error.response)) {
          this.handleGQLError(
            new GQLResponseError('GQL Error', error.response.data.errors)
          );
        } else if (error.code && error.code === timeoutErrorCode) {
          this.handleTimeoutError();
        } else {
          this.handleAjaxError(error);
        }

        throw error;
      }
    );
  }

  render() {
    return (
      <React.Fragment>
        <IdleTimer
          crossTab={true}
          ref={this.idleTimer}
          element={document}
          onActive={this.handleOnActive}
          onAction={this.handleOnAction}
          onIdle={this.handleOnIdle}
          timeout={
            (this.props.ConfigStore.config.idleStateTimeoutInSec / 2) * 1000
          }
          startOnMount={true}
        />
        <div id="axios-wrapper">{this.state.ready && this.props.children}</div>
      </React.Fragment>
    );
  }
}

export default injectIntl(AppConfig);



// WEBPACK FOOTER //
// ./src/containers/AppConfig/AppConfig.jsx